import { Alert, Button, Card, Form, Input, Select, Table } from 'antd'
import React, { useEffect, useState } from 'react'
import { ItemResponseKeywordStatsBeanObject, KeywordStatsBean, ListPagination } from 'src/api/api'
import { toSort, useSorter } from 'src/util/useSorter'
import contextualTargetingApi from '../contextualTargeting/ContextualTargetingApi'
import { formatNumberWithGrouping } from '../report/columnsHelper'
import { KeywordReportPeriod, keywordReportPeriodLabels, periodDates } from './keywordReportRequest'

const FormItem = Form.Item
const Option = Select.Option
const Column = Table.Column

type Query = { period: KeywordReportPeriod, keyword?: string }

export const KeywordReport: React.FC = () => {
    
    const [form] = Form.useForm()

    const [error, setError] = useState<string>()
    const [loading, setLoading] = useState<boolean>(false)

    const [data, setData] = useState<KeywordStatsBean[]>()
    const [pagination, setPagination] = useState<any>({
        defaultCurrent: 1,
        defaultPageSize: 50,
        current: 1
    })

    const [query, setQuery] = useState<Query>()

    const loadData = async (page: number) => {
        if (!query) return

        setError(undefined)
        setLoading(true)
        try {
            const { start, end } = periodDates(query.period)
            const response = await contextualTargetingApi.keywordStats(pagination.defaultPageSize, page, toSort(sorter), query.keyword, start, end)

            const array = response.data as Array<ItemResponseKeywordStatsBeanObject>
            const newPagination = response.pagination as ListPagination

            setData(array.map(item => item.data as KeywordStatsBean))
            setPagination({ ...pagination, total: newPagination.total, current: newPagination.currentPage })
        } catch (e: any) {
            setError(e?.error ?? 'Failed to load report')
        } finally {
            setLoading(false)
        }
    }

    const { sorter, handleTableChange } = useSorter(pagination, loadData, 'keyword', 'asc')

    useEffect(() => {
        loadData(1)
    }, [query, sorter])

    const onFinish = (values: any) => setQuery({
        period: values.period,
        keyword: values.keyword?.trim() || undefined,
    })

    const renderNumber = (value?: number) => value == null ? value : formatNumberWithGrouping(value)

    return (
        <div>
            <Form
                style={{ maxWidth: '1000px', margin: '0 auto' }}
                onFinish={onFinish}
                form={form}
                initialValues={{ period: KeywordReportPeriod.Last7Days }}
                labelCol={{ span: 3 }}
                wrapperCol={{ span: 21 }}
            >
                {error && <Alert message={error} type="error" />}

                <Card size="small" title="Keyword Report" className="form-card">
                    <FormItem label="Period" name="period" rules={[{ required: true, message: 'Select period' }]}>
                        <Select placeholder="Select period">
                            {Object.values(KeywordReportPeriod).map((p) => (
                                <Option key={p} value={p} title={keywordReportPeriodLabels[p]}>
                                    {keywordReportPeriodLabels[p]}
                                </Option>
                            ))}
                        </Select>
                    </FormItem>

                    <FormItem label="Keyword" name="keyword">
                        <Input allowClear placeholder="Enter keyword" autoComplete="off" />
                    </FormItem>

                    <FormItem wrapperCol={{ offset: 3, span: 21 }}>
                        <Button type="primary" htmlType="submit" loading={loading}>
                            Generate report
                        </Button>
                    </FormItem>
                </Card>
            </Form>

            {data && (
                <Table
                    rowKey="keyword"
                    size="middle"
                    dataSource={data}
                    loading={loading}
                    pagination={{ ...pagination, size: 'default' }}
                    onChange={handleTableChange}
                >
                    <Column key="keyword" title="Keyword" dataIndex="keyword" sorter defaultSortOrder="ascend" />
                    <Column key="pageUrls" title="Page URLs" dataIndex="pageUrls" sorter render={renderNumber} />
                    <Column key="requests" title="Requests" dataIndex="requests" sorter render={renderNumber} />
                </Table>
            )}
        </div>
    )
}



// WEBPACK FOOTER //
// ./src/component/app/keywordReport/KeywordReport.tsx